export type AppRole =
  | "consumer"
  | "executor"
  | "emergency_contact"
  | "trustee"
  | "guardian"
  | "beneficiary"
  | "auditor"
  | "notary"
  | "partner_viewer"
  | "partner_processor"
  | "partner_admin"
  | "platform_admin"
  | "org_owner"
  | "org_admin";

/**
 * Account types that users can select during signup
 */
export type AccountType = "individual" | "institution" | "partner";

/**
 * Map account type to default role(s)
 * Users select account type during signup, role is assigned server-side
 */
export const ACCOUNT_TYPE_TO_ROLES: Record<AccountType, AppRole[]> = {
  individual: ["consumer"],
  institution: ["consumer"], // Institutions start as consumers
  partner: ["partner_viewer"], // Partners start with viewer role
};

export const ZONE_HOME: Record<AppRole, string> = {
  consumer: "/app",
  executor: "/app",
  emergency_contact: "/app",
  trustee: "/app",
  guardian: "/app",
  beneficiary: "/app",
  auditor: "/enterprise",
  notary: "/partner",
  partner_viewer: "/partner",
  partner_processor: "/partner",
  partner_admin: "/partner",
  platform_admin: "/admin",
  org_owner: "/enterprise",
  org_admin: "/enterprise",
};

export function hasAnyRole(userRoles: AppRole[], allowed: AppRole[]): boolean {
  return userRoles.some(r => allowed.includes(r));
}

/**
 * Extract user roles from Supabase User metadata
 * Checks both user_metadata and app_metadata for roles in various formats.
 * Also infers a default role from account_type when no explicit roles exist,
 * ensuring partners / institutions always get at least a base role.
 */
export function extractUserRoles(user: { user_metadata?: Record<string, unknown>; app_metadata?: Record<string, unknown> } | null): AppRole[] {
  if (!user) return [];
  
  const userMetadata = user.user_metadata ?? {};
  const appMetadata = user.app_metadata ?? {};
  
  // Try to extract roles array
  let roles: string[] = [];
  
  // Check JWT claims from custom_access_token_hook (stored in app_metadata.roles)
  if (Array.isArray(appMetadata.roles)) {
    roles = [...roles, ...(appMetadata.roles as string[])];
  }
  
  // Check user_metadata.roles (array format)
  if (Array.isArray(userMetadata.roles)) {
    roles = [...roles, ...(userMetadata.roles as string[])];
  }
  
  // Check single role in user_metadata.role (string format)
  if (typeof userMetadata.role === 'string' && userMetadata.role && !roles.includes(userMetadata.role)) {
    roles.push(userMetadata.role);
  }
  
  // Check single role in app_metadata.role (string format)
  if (typeof appMetadata.role === 'string' && appMetadata.role && !roles.includes(appMetadata.role)) {
    roles.push(appMetadata.role);
  }
  
  // Remove duplicates and empty values
  const uniqueRoles = [...new Set(roles)].filter(Boolean);
  
  // If no explicit roles found, infer from account_type so that
  // partner / enterprise users always receive at least a base role.
  if (uniqueRoles.length === 0) {
    const accountType = (userMetadata.account_type ?? appMetadata.account_type) as string | undefined;
    if (accountType && accountType in ACCOUNT_TYPE_TO_ROLES) {
      return [...ACCOUNT_TYPE_TO_ROLES[accountType as AccountType]];
    }
  }
  
  return uniqueRoles as AppRole[];
}

export function getZoneFromPath(path: string): "public" | "app" | "partner" | "admin" | "auth" | "enterprise" {
  if (path.startsWith("/enterprise")) return "enterprise";
  if (path.startsWith("/app")) return "app";
  if (path.startsWith("/partner")) return "partner";
  if (path.startsWith("/admin")) return "admin";
  if (path.startsWith("/login") || path.startsWith("/register") || path.startsWith("/verify")) return "auth";
  return "public";
}

export const CONSUMER_ROLES: AppRole[] = ["consumer", "executor", "emergency_contact", "trustee", "guardian", "beneficiary"];
export const PARTNER_ROLES: AppRole[] = ["partner_viewer", "partner_processor", "partner_admin", "notary"];
export const ADMIN_ROLES: AppRole[] = ["platform_admin"];
export const ENTERPRISE_ROLES: AppRole[] = ["org_owner", "org_admin", "auditor"];

/**
 * All zones an admin can access. Admins have unrestricted zone access
 * and can choose which zone to operate in.
 */
export type AdminZone = "admin" | "app" | "partner" | "enterprise";

export const ALL_ADMIN_ZONES: { zone: AdminZone; label: string; path: string }[] = [
  { zone: "admin", label: "Admin Panel", path: "/admin" },
  { zone: "app", label: "User App", path: "/app" },
  { zone: "partner", label: "Partner Portal", path: "/partner" },
  { zone: "enterprise", label: "Enterprise", path: "/enterprise" },
];

const ADMIN_ZONE_KEY = "aegis_admin_zone";

/** Persist the admin's selected zone to localStorage */
export function setAdminSelectedZone(zone: AdminZone): void {
  try {
    localStorage.setItem(ADMIN_ZONE_KEY, zone);
  } catch { /* storage unavailable */ }
}

/** Read the admin's persisted zone selection (defaults to 'admin') */
export function getAdminSelectedZone(): AdminZone {
  try {
    const stored = localStorage.getItem(ADMIN_ZONE_KEY);
    if (stored && ["admin", "app", "partner", "enterprise"].includes(stored)) {
      return stored as AdminZone;
    }
  } catch { /* storage unavailable */ }
  return "admin";
}

/** Clear admin zone selection (on sign-out) */
export function clearAdminSelectedZone(): void {
  try {
    localStorage.removeItem(ADMIN_ZONE_KEY);
  } catch { /* storage unavailable */ }
}

/**
 * Check if a user with the given roles is a platform admin.
 */
export function isAdminUser(roles: AppRole[]): boolean {
  return roles.some(r => ADMIN_ROLES.includes(r));
}
